import type { ReactNode } from 'react';

import { Breadcrumb, type Crumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Field, Textarea } from '@/components/fields';
import { Notice } from '@/components/primitives';
import { Timestamp } from '@/components/Timestamp';
import type { ComponentConfig, ScopeConfig } from '@/domain/types';

export function ReasonField({
  value,
  onChange,
  error,
  disabled,
  placeholder = 'e.g. The job takes 6 minutes; the first step fired too early.',
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <Field label="Reason" required error={error}>
      {(props) => (
        <Textarea
          {...props}
          name="reason"
          rows={2}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
        />
      )}
    </Field>
  );
}

/** Shown when the row moved under the editor: a 409, or a background refetch with a newer version. */
export function StaleNotice({
  current,
  onLoad,
  disabled,
}: {
  current: ComponentConfig | ScopeConfig;
  onLoad: () => void;
  disabled?: boolean;
}) {
  return (
    <Notice tone="warning">
      <p className="font-medium">
        Version {current.version} was saved by {current.updated_by} (
        <Timestamp iso={current.updated_at} mode="auto" />) while you were editing. Nothing of yours
        has been saved.
      </p>
      <div className="mt-2">
        <Button size="sm" onClick={onLoad} disabled={disabled}>
          Load version {current.version}
        </Button>
      </div>
    </Notice>
  );
}

/** The header of a sub-page under a scope: breadcrumb, a 17px title, and its actions on the right. */
export function PageHeader({
  title,
  description,
  actions,
  breadcrumb,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumb?: Crumb[];
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        {breadcrumb ? <Breadcrumb items={breadcrumb} className="mb-1" /> : null}
        <h2 className="text-heading font-medium">{title}</h2>
        {description ? (
          <p className="text-body text-ink-2 mt-1 max-w-[80ch]">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2 shrink-0">{actions}</div> : null}
    </div>
  );
}
